/* eslint-disable react/prop-types */

// * Imports
import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// * Component imports
import FixedBanner from "../../components/FixedBanner/FixedBanner";
import WarningIcon from "@mui/icons-material/Warning";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BoltIcon from "@mui/icons-material/Bolt";
import PostModal from "../../components/PostModal/PostModal";
import { stripHtmlToText } from "../../utils/postBodyUtils";

// * Environment Imports
import { environment } from "../../environments/environment";

// * Import styles
import "./ExecutionDetail.css";

// * Import Context
import { useAuthContext } from "../../hooks/useAuthContext";

//TODO: Handle no posts found UI
//TODO: Show proper HTML with spacing and list

const ExecutionPage = () => {
  document.title = `Execution | LeadZLab`;
  const navigate = useNavigate();
  const location = useLocation();

  const [posts, setPosts] = useState([]);
  const [job, setJob] = useState({
    jobName: "",
    createdAt: "",
  });
  const [dailyPostLimitReached, setDailyPostLimitReached] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const [expandedPostId, setExpandedPostId] = useState(null);
  const [expandedReasonPostId, setExpandedReasonPostId] = useState(null);
  const [selectedPosts, setSelected] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  // Sort mode: "newest" (default), "intent", "upvotes", "budget"
  const [sortMode, setSortMode] = useState("newest");

  // * Plan details
  const [plan, setPlan] = useState("");
  const [trialValidUntil, setTrialValidUntil] = useState(false);

  const [objective, setObjective] = useState("");
  const [linkedInQuery, setLinkedInQuery] = useState("");
  const [subReddits, setSubreddits] = useState([]);
  const [jobType, setJobType] = useState("linkedin");

  // Read status filter: "all" | "unread" | "read"
  const [readStatusFilter, setReadStatusFilter] = useState("all");

  const togglePostExpansion = (postId) => {
    updateReadStatus(true, false, [postId]);

    // Check if the modal is currently open for the clicked post (compare as strings for reliable match)
    if (isModalOpen && String(expandedPostId) === String(postId)) {
      setIsModalOpen(false);
    } else {
      setIsModalOpen(true);
      setExpandedPostId(postId);
    }
  };

  // * Auth context
  const { user } = useAuthContext();

  function parseSubredditsFromQuery(redditQuery) {
    const subredditsRegex = /subreddit:(\w+)/g;
    const subreddits = [];
    let match;
    while ((match = subredditsRegex.exec(redditQuery || "")) !== null) {
      subreddits.push(match[1]);
    }
    return subreddits;
  }

  // * Intent: prefer LLM relevance score when present (relevanceScore 0–100), else fall back to upvotes
  const getIntentLevel = (post) => {
    if (post.relevanceScore != null && typeof post.relevanceScore === "number") {
      if (post.relevanceScore >= 70) return "high";
      if (post.relevanceScore >= 40) return "medium";
      return "low";
    }
    const up = typeof post.upvotes === "number" ? post.upvotes : parseInt(post.upvotes, 10) || 0;
    if (up >= 15) return "high";
    if (up >= 5) return "medium";
    return "low";
  };

  // * Sort score: LLM relevance (0–100) or intent tier (3/2/1) for default sort
  const getIntentScore = (post) => {
    if (post.relevanceScore != null && typeof post.relevanceScore === "number") {
      return post.relevanceScore;
    }
    const level = getIntentLevel(post);
    return level === "high" ? 3 : level === "medium" ? 2 : 1;
  };

  const hasBudgetMentioned = (post) => {
    const body = (post.body || "").toLowerCase();
    return /\$|\bbudget\b/.test(body);
  };

  const hasHiring = (post) => {
    const body = (post.body || "").toLowerCase();
    const title = (post.postTitle || "").toLowerCase();
    return /\bhiring\b|\blooking for\b|\bneed (a|someone)\b/.test(body + " " + title);
  };

  /** Ordered list of tag labels for a post (same order for every post for consistent UI) */
  const getPostTags = (post) => {
    const tags = [];
    if (hasBudgetMentioned(post)) tags.push("Budget mentioned");
    if (hasHiring(post)) tags.push("Hiring");
    return tags;
  };

  /** Single canonical relevance/intent label for display */
  const getIntentLabel = (post, intent) => {
    if (post.relevanceScore != null && typeof post.relevanceScore === "number") {
      return `${post.relevanceScore}%`;
    }
    return intent === "high" ? "High intent" : intent === "medium" ? "Medium intent" : "Low intent";
  };

  /** Tooltip explaining why this intent/relevance level */
  const getIntentTooltip = (post) => {
    if (post.relevanceScore != null && typeof post.relevanceScore === "number") {
      const reason = post.relevanceReason ? ` — ${post.relevanceReason}` : "";
      return `Relevance: ${post.relevanceScore}/100${reason}`;
    }
    const up = typeof post.upvotes === "number" ? post.upvotes : parseInt(post.upvotes, 10) || 0;
    if (up >= 15) return `High intent: ${up} upvotes (15+ upvotes)`;
    if (up >= 5) return `Medium intent: ${up} upvotes (5–14 upvotes)`;
    return `Low intent: ${up} upvote${up !== 1 ? "s" : ""} (under 5 upvotes)`;
  };

  // * Visible posts: "all" = everyone; "unread" = all but sorted unread first (row moves down when read); "read" = read only
  const visiblePosts =
    readStatusFilter === "read"
      ? posts.filter((p) => !!p.is_read)
      : posts;

  // * Unread-first sort: when filter is "unread", unread posts stay on top, read posts below (clicked row "moves down")
  const sortByUnreadFirst = (a, b) => ((a.is_read ? 1 : 0) - (b.is_read ? 1 : 0));

  // * Sorted posts for Reddit (by sortMode), operates on visible posts; unread first when filter is "unread"
  const getSortedRedditPosts = () => {
    const copy = [...visiblePosts];
    const byUnreadFirst = readStatusFilter === "unread" ? sortByUnreadFirst : () => 0;
    switch (sortMode) {
      case "newest":
        return copy.sort((a, b) => byUnreadFirst(a, b) || Date.parse(b.post_created_at) - Date.parse(a.post_created_at));
      case "upvotes":
        return copy.sort((a, b) => byUnreadFirst(a, b) || (b.upvotes || 0) - (a.upvotes || 0));
      case "budget":
        return copy.sort((a, b) => byUnreadFirst(a, b) || (hasBudgetMentioned(b) ? 1 : 0) - (hasBudgetMentioned(a) ? 1 : 0) || Date.parse(b.post_created_at) - Date.parse(a.post_created_at));
      default:
        return copy.sort((a, b) => {
          const scoreA = getIntentScore(a);
          const scoreB = getIntentScore(b);
          return byUnreadFirst(a, b) || (scoreB !== scoreA ? scoreB - scoreA : Date.parse(b.post_created_at) - Date.parse(a.post_created_at));
        });
    }
  };

  // * Fetch execution data; redirect to job leads view (leads are now shown per scout)
  const getExecutions = async () => {
    try {
      setLoading(true);
      const searchParams = new URLSearchParams(location.search);
      const executionId = searchParams.get("execution_id");
      let { data } = await axios.get(
        `${environment.BACKEND}/resources/executions/posts/${executionId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      const jobId = data?.data?.job?._id;
      if (jobId) {
        setRedirecting(true);
        navigate(`/job-detail?job_id=${jobId}`, { replace: true });
        return;
      }
      setRedirecting(false);
      setPosts(data.data.posts);
      setJobType(data.data.job.type);
      setDailyPostLimitReached(!!data.data.execution?.daily_post_limit_reached);
      if (data.data.job.type === "reddit") {
        setSubreddits(parseSubredditsFromQuery(data.data.job.q));
        setObjective(data.data.job.objective || "");
      } else {
        setLinkedInQuery(data.data.job.q || "");
      }

      setJob(data.data.job);
      setLoading(false);
    } catch (err) {
      setRedirecting(false);
      setLoading(false);
      console.log(err.response.data);
    }
  };

  // * Handle select
  const handleSelect = (event) => {
    let updatedList = [...selectedPosts];
    if (event.target.checked)
      updatedList = [...selectedPosts, event.target.value];
    else updatedList.splice(selectedPosts.indexOf(event.target.value), 1);
    setSelected(updatedList);
  };

  const selectAll = () => {
    if (selectedPosts.length) setSelected([]);
    else {
      const list = visiblePosts.map((post) => String(post._id));
      setSelected(list);
    }
  };

  // * Check if selected (same row styling as Job Detail)
  const isPostSelected = (postId) =>
    selectedPosts.some((id) => String(id) === String(postId));

  // * Single row renderer matching Job Detail table structure (same modal on row click, same "View" link)
  const renderLeadRow = (post, opts = {}) => {
    const showRelevanceColumn = opts.showRelevanceColumn !== false;
    const isUnread = !post.is_read;
    const intent = getIntentLevel(post);
    const intentBarClass =
      intent === "high" ? "execution-row-bar-high" :
      intent === "medium" ? "execution-row-bar-medium" : "execution-row-bar-low";
    const link = post.postLink || post.linkedInProfileURL || "#";
    const isLinkedIn = jobType === "linkedin";
    const displayName = isLinkedIn ? (post.user || "—") : (post.postTitle || post.user || "Lead");
    const subLabel = !isLinkedIn && post.subreddit_name
      ? (post.subreddit_name.startsWith("r/") ? post.subreddit_name : "r/" + post.subreddit_name)
      : null;
    const bodyPreview = isLinkedIn ? stripHtmlToText(post.body || "").trim() : "";
    const bodySnippet = bodyPreview.slice(0, 180);
    return (
      <tr
        key={post._id}
        className={[
          "execution-row",
          isUnread ? "execution-row-unread" : "execution-row-read",
          isPostSelected(post._id) ? "execution-row-selected" : "",
          intentBarClass,
        ].filter(Boolean).join(" ")}
        onClick={() => togglePostExpansion(post._id)}
        onKeyDown={(e) => e.key === "Enter" && togglePostExpansion(post._id)}
        role="button"
        tabIndex={0}
        aria-label={isLinkedIn ? `View ${post.user}` : `View ${displayName}`}
      >
        <td className="execution-cell-checkbox" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
            onChange={handleSelect}
            checked={isPostSelected(post._id)}
            value={String(post._id)}
            aria-label={isLinkedIn ? `Select ${post.user}` : `Select ${displayName}`}
          />
        </td>
        <td className="execution-cell-title">
          <div className="execution-row-title-block">
            <span className="execution-row-dot-slot">
              {isUnread ? <span className="execution-row-dot" aria-hidden /> : null}
            </span>
            <div className="execution-row-title-content">
              <div className="execution-row-title-line">
                {isLinkedIn ? (
                  <a
                    href={post.linkedInProfileURL || link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`execution-row-title-text no-underline ${isUnread ? "font-semibold text-slate-900 hover:text-emerald-600" : "font-medium text-slate-700 hover:text-emerald-600"}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {post.user || "—"}
                  </a>
                ) : (
                  <span className={`execution-row-title-text ${isUnread ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`} title={displayName}>
                    {displayName}
                  </span>
                )}
                {isUnread && <span className="execution-pill-new">NEW</span>}
              </div>
              <p className="execution-row-meta">
                {isLinkedIn ? (
                  <>
                    {post.post_created_at ? (
                      <>
                        {new Date(Date.parse(post.post_created_at)).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}
                        {getRelativeTime(post.post_created_at) && (
                          <>
                            <span className="execution-meta-sep">·</span>
                            <span className="execution-meta-ago">{getRelativeTime(post.post_created_at)}</span>
                          </>
                        )}
                      </>
                    ) : "—"}
                  </>
                ) : (
                  <>
                    {subLabel && (
                      <>
                        <a
                          href={`https://www.reddit.com/${subLabel}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-600 hover:text-emerald-700 no-underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {subLabel}
                        </a>
                        <span className="execution-meta-sep">·</span>
                      </>
                    )}
                    <span>
                      {post.post_created_at && new Date(Date.parse(post.post_created_at)).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}
                      {getRelativeTime(post.post_created_at) && (
                        <>
                          <span className="execution-meta-sep">·</span>
                          <span className="execution-meta-ago">{getRelativeTime(post.post_created_at)}</span>
                        </>
                      )}
                    </span>
                    {post.upvotes != null && (
                      <>
                        <span className="execution-meta-sep">·</span>
                        <span>{post.upvotes ?? 0} upvotes</span>
                      </>
                    )}
                  </>
                )}
              </p>
              {!isLinkedIn && (
                <div className="execution-row-tags">
                  {getPostTags(post).map((tag) => (
                    <span key={tag} className="execution-pill-tag execution-tooltip-trigger" title={tag === "Budget mentioned" ? "Post mentions budget or $ amount" : "Post mentions hiring or looking for help"}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </td>
        {isLinkedIn && (
          <td className="execution-cell-preview">
            <p className="text-sm text-slate-600 line-clamp-2" title={bodyPreview || undefined}>
              {bodySnippet ? (bodySnippet + (bodyPreview.length > bodySnippet.length ? "…" : "")) : "—"}
            </p>
          </td>
        )}
        {showRelevanceColumn && (
          <td className="execution-cell-intent" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col gap-0.5">
              {post.relevanceScore != null ? (
                post.relevanceReason && (post.relevanceScore != null || jobType === "linkedin") ? (
                  <>
                    <button
                      type="button"
                      className={`execution-pill-intent intent-${intent} execution-pill-intent-clickable text-left cursor-pointer`}
                      onClick={() => setExpandedReasonPostId((prev) => (prev === post._id ? null : post._id))}
                      title="Click to show why AI marked this lead"
                    >
                      {getIntentLabel(post, intent)}
                    </button>
                    {expandedReasonPostId === post._id && (
                      <p className="execution-row-llm-reason text-xs text-slate-600 mt-0.5 pr-1 max-w-[240px]">
                        {post.relevanceReason}
                      </p>
                    )}
                  </>
                ) : (
                  <span className={`execution-pill-intent intent-${intent}`} title={getIntentTooltip(post)}>
                    {getIntentLabel(post, intent)}
                  </span>
                )
              ) : (
                <span className="text-slate-400 text-sm">—</span>
              )}
            </div>
          </td>
        )}
        <td className="execution-cell-action" onClick={(e) => e.stopPropagation()}>
          <div className="execution-row-actions">
            <a href={link} target="_blank" rel="noopener noreferrer" className="execution-btn-view" onClick={(e) => e.stopPropagation()}>
              View
            </a>
          </div>
        </td>
      </tr>
    );
  };

  // * Update read status
  const updateReadStatus = async (
    is_read,
    updateSelectedPosts = true,
    posts = []
  ) => {
    try {
      await axios.put(
        `${environment.BACKEND}/resources/post/read-status`,
        {
          postIds: updateSelectedPosts ? selectedPosts : posts,
          is_read,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );

      // * Update to is_read
      if (updateSelectedPosts) {
        setPosts((prevPosts) => {
          return prevPosts.map((post) => {
            if (selectedPosts.includes(post._id)) {
              return { ...post, is_read };
            }
            return post;
          });
        });
        setSelected([]);
      } else {
        const idSet = new Set(posts.map((id) => String(id)));
        setPosts((prevPosts) =>
          prevPosts.map((post) => (idSet.has(String(post._id)) ? { ...post, is_read } : post))
        );
      }
    } catch (err) {
      window.alert(err.response.data.message);
    }
  };

  /** Relative time from now, e.g. "10 min ago", "2 hours ago", "3 days ago" (calculated at render) */
  function getRelativeTime(dateString) {
    if (!dateString) return "";
    const date = new Date(Date.parse(dateString));
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? "s" : ""} ago`;
    return "";
  }

  function calculateDateDifference(timestamp) {
    const now = new Date();
    const targetDate = new Date(timestamp);

    // Check if the target date is in the past
    if (targetDate < now) {
      return -1;
    }

    const timeDifference = targetDate - now;
    const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

    return daysDifference;
  }

  // * Fetch plan
  const getPlan = async () => {
    try {
      const { data } = await axios.get(`${environment.BACKEND}/user`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      const userData = data.data;
      setPlan(userData.plan);

      setTrialValidUntil(
        calculateDateDifference(userData.subscriptionActiveTill)
      );
    } catch (err) {
      console.log(err.response.data);
    }
  };

  useEffect(() => {
    getExecutions();
    getPlan();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsModalOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Lock body scroll when modal is open: fixed body + preserve scroll position (reliable on desktop and mobile)
  useEffect(() => {
    if (isModalOpen) {
      const scrollY = window.scrollY ?? window.pageYOffset;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      const prevStyle = {
        overflow: document.body.style.overflow,
        paddingRight: document.body.style.paddingRight,
        position: document.body.style.position,
        top: document.body.style.top,
        left: document.body.style.left,
        right: document.body.style.right,
        width: document.body.style.width,
      };
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : "";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
      return () => {
        Object.assign(document.body.style, prevStyle);
        window.scrollTo(0, scrollY);
      };
    }
  }, [isModalOpen]);

  const dateOpts = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };

  if (redirecting) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <p className="text-slate-600">Redirecting to scout…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <NavLink
          to="/jobs"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 no-underline transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
          Back to scouts
        </NavLink>
        {plan === "free" ? (
          trialValidUntil < 0 ? (
            <FixedBanner
              icon={<WarningIcon />}
              message={`Your free trial has expired. Please upgrade now to continue generating leads`}
              actionText="Upgrade"
              actionLink="/pricing"
              color="red"
            />
          ) : (
            <FixedBanner
              icon={<BoltIcon />}
              message={`Your free trial expires in ${trialValidUntil} days`}
              actionText="Upgrade"
              actionLink="/pricing"
              color="emerald"
            />
          )
        ) : plan === "" ? null : (
          <FixedBanner
            icon={<VerifiedUserIcon />}
            message={`You are currently on the ${plan?.toUpperCase()} plan`}
            color="yellow"
          />
        )}

        {/* Execution summary card */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                {loading ? (
                  <Skeleton width={220} height={28} className="rounded" />
                ) : (
                  <NavLink
                    to={`/job-detail?job_id=${job._id}`}
                    className="text-xl font-semibold text-slate-900 hover:text-emerald-600 no-underline transition-colors"
                  >
                    {job.jobName}
                  </NavLink>
                )}
                <p className="mt-1 text-sm text-slate-500">
                  {!loading && job.createdAt
                    ? `Run ${new Date(job.createdAt).toLocaleString([], dateOpts)}`
                    : "—"}
                  {!loading && posts.length >= 0 && (
                    <span className="ml-2">· {posts.length} post{posts.length !== 1 ? "s" : ""} found</span>
                  )}
                </p>
              </div>
              <div className="flex flex-wrap gap-3 text-sm">
                {!loading && job.createdAt && (
                  <span className="inline-flex items-center rounded-lg bg-slate-100 px-3 py-1.5 text-slate-600">
                    {new Date(job.createdAt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}
                  </span>
                )}
                {!loading && (
                  <span className="inline-flex items-center rounded-lg bg-emerald-50 text-emerald-700 px-3 py-1.5 font-medium">
                    {posts.length} lead{posts.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>
            {jobType === "reddit" && (
              <div className="mt-6 pt-6 border-t border-slate-100 space-y-4">
                {objective && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Objective</p>
                    <p className="text-sm text-slate-700 leading-relaxed">{objective}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Subreddits</p>
                  {loading ? (
                    <Skeleton count={1} width={180} height={24} className="rounded-full" />
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      {subReddits.map((sub, index) => (
                        <span key={index} className="inline-flex items-center rounded-full bg-slate-800 text-white px-3 py-1 text-xs font-medium">
                          r/{sub}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {jobType === "linkedin" && linkedInQuery && (
              <div className="mt-6 pt-6 border-t border-slate-100">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Search query</p>
                <p className="text-sm text-slate-700">{linkedInQuery}</p>
              </div>
            )}
          </div>
        </div>

      {dailyPostLimitReached && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-amber-800">
            <strong>Daily post limit reached.</strong> Your scouts have found the maximum posts for today. Want them to keep looking? Upgrade for more posts per day.
          </p>
          <NavLink to="/pricing" className="shrink-0 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700">
            Upgrade
          </NavLink>
        </div>
      )}

      {/* Reddit table */}
      {jobType === "reddit" && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-900">Reddit posts</h2>
            {!loading && posts.length > 0 && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs font-medium text-slate-500">Read status</span>
                <select
                  value={readStatusFilter}
                  onChange={(e) => setReadStatusFilter(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  aria-label="Filter by read status"
                >
                  <option value="all">All</option>
                  <option value="unread">Unread first</option>
                  <option value="read">Read only</option>
                </select>
                <span className="text-xs font-medium text-slate-500">Sort</span>
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  aria-label="Sort posts"
                >
                  <option value="newest">Newest</option>
                  <option value="intent">Highest relevance</option>
                  <option value="upvotes">Most upvoted</option>
                  <option value="budget">Budget mentioned</option>
                </select>
              </div>
            )}
          </div>
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <Skeleton count={6} height={48} className="rounded-lg" />
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-12 text-center">
              <p className="font-medium text-slate-900">No posts in this run</p>
              <p className="mt-2 text-sm text-slate-600">This execution didn’t find any matching Reddit posts.</p>
            </div>
          ) : visiblePosts.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-12 text-center">
              <p className="font-medium text-slate-900">No read posts</p>
              <p className="mt-2 text-sm text-slate-600">Switch to &quot;All&quot; or &quot;Unread first&quot; to see posts.</p>
              <button type="button" onClick={() => setReadStatusFilter("all")} className="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Show all</button>
            </div>
          ) : (
            <div className={`execution-table-wrap comfortable ${isModalOpen ? "overflow-hidden" : ""}`}>
              {selectedPosts.length > 0 && (
                <div className="execution-table-sticky-bar">
                  <span className="text-sm font-medium text-slate-700">
                    {selectedPosts.length} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="execution-table-bar-btn secondary"
                      onClick={() => updateReadStatus(false)}
                    >
                      Mark unread
                    </button>
                    <button
                      type="button"
                      className="execution-table-bar-btn primary"
                      onClick={() => updateReadStatus(true)}
                    >
                      <MarkEmailReadOutlinedIcon fontSize="small" />
                      Mark read
                    </button>
                  </div>
                </div>
              )}
              <table className="execution-table">
                <thead>
                  <tr>
                    <th className="execution-th-checkbox" scope="col">
                      <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" onClick={selectAll} aria-label="Select all" />
                    </th>
                    <th className="execution-th-title" scope="col">Title</th>
                    <th className="execution-th-intent" scope="col">Relevance</th>
                    <th className="execution-th-action" scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedRedditPosts().map((post) => renderLeadRow(post))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* LinkedIn table */}
      {jobType === "linkedin" && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-900">LinkedIn posts</h2>
            {!loading && posts.length > 0 && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs font-medium text-slate-500">Read status</span>
                <select
                  value={readStatusFilter}
                  onChange={(e) => setReadStatusFilter(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  aria-label="Filter by read status"
                >
                  <option value="all">All</option>
                  <option value="unread">Unread first</option>
                  <option value="read">Read only</option>
                </select>
                <span className="text-xs font-medium text-slate-500">Sort</span>
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  aria-label="Sort LinkedIn posts"
                >
                  <option value="newest">Newest</option>
                  <option value="intent">Highest relevance</option>
                </select>
              </div>
            )}
          </div>
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <Skeleton count={6} height={48} className="rounded-lg" />
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-12 text-center">
              <p className="font-medium text-slate-900">No posts in this run</p>
              <p className="mt-2 text-sm text-slate-600">This execution didn’t find any matching LinkedIn posts.</p>
            </div>
          ) : visiblePosts.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-12 text-center">
              <p className="font-medium text-slate-900">No read posts</p>
              <p className="mt-2 text-sm text-slate-600">Switch to &quot;All&quot; or &quot;Unread first&quot; to see posts.</p>
              <button type="button" onClick={() => setReadStatusFilter("all")} className="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Show all</button>
            </div>
          ) : (
            <div className={`execution-table-wrap comfortable ${isModalOpen ? "overflow-hidden" : ""}`}>
              {selectedPosts.length > 0 && (
                <div className="execution-table-sticky-bar">
                  <span className="text-sm font-medium text-slate-700">{selectedPosts.length} selected</span>
                  <div className="flex items-center gap-2">
                    <button type="button" className="execution-table-bar-btn secondary" onClick={() => updateReadStatus(false)}>Mark unread</button>
                    <button type="button" className="execution-table-bar-btn primary" onClick={() => updateReadStatus(true)}>
                      <MarkEmailReadOutlinedIcon fontSize="small" /> Mark read
                    </button>
                  </div>
                </div>
              )}
              <table className="execution-table">
                <thead>
                  <tr>
                    <th className="execution-th-checkbox" scope="col">
                      <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" onClick={selectAll} aria-label="Select all" />
                    </th>
                    <th className="execution-th-title" scope="col">Posted by</th>
                    <th className="execution-th-preview" scope="col">Preview</th>
                    {visiblePosts.some((p) => p.relevanceScore != null) && (
                      <th className="execution-th-intent" scope="col">Relevance</th>
                    )}
                    <th className="execution-th-action" scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const list = [...visiblePosts];
                    const unreadFirst = readStatusFilter === "unread" ? sortByUnreadFirst : () => 0;
                    const byIntent = (a, b) => (b.relevanceScore ?? 0) - (a.relevanceScore ?? 0) || Date.parse(b.post_created_at || 0) - Date.parse(a.post_created_at || 0);
                    const byNewest = (a, b) => Date.parse(b.post_created_at || 0) - Date.parse(a.post_created_at || 0);
                    const sorted = sortMode === "intent" && visiblePosts.some((p) => p.relevanceScore != null)
                      ? list.sort((a, b) => unreadFirst(a, b) || byIntent(a, b))
                      : list.sort((a, b) => unreadFirst(a, b) || byNewest(a, b));
                    return sorted.map((post) =>
                      renderLeadRow(post, { showRelevanceColumn: visiblePosts.some((p) => p.relevanceScore != null) })
                    );
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {isModalOpen && (
        <PostModal
          post={posts.find((p) => String(p._id) === String(expandedPostId)) ?? null}
          onClose={() => setIsModalOpen(false)}
          isLinkedIn={jobType === "linkedin"}
          showAiOutreach={true}
          jobObjective={objective}
          accessToken={user?.accessToken}
        />
      )}
      </div>
    </div>
  );
};

export default ExecutionPage;
